import { useEffect, useState } from "react";
import { ChevronLeft } from "lucide-react";
import type { SubmitHandler } from "react-hook-form";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import { RecoveryCodes, TwoFactorCodeChallenge } from "@/components";
import { useAuthentication } from "@/hooks";
import { ROUTES } from "@/router";
import type { TwoFactorAuthFormValuesType } from "@/types";
import { svgToBase64 } from "@/utils";

export const TwoFactorAuthSetup = () => {
  const [showQr, setShowQr] = useState(true);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [qrCode, setQrCode] = useState("");

  const { useEnableTwoFactor, useConfirmTwoFactor, useChallengeTwoFactor } =
    useAuthentication();
  const { mutate: enable2fa, isPending } = useEnableTwoFactor();
  const { mutate: confirm2fa, isError: hasConfirmFaError } =
    useConfirmTwoFactor();
  const {
    mutate: challenge2fa,
    isError: hasChallengeFaError,
    isPending: isLoadingChallenge,
  } = useChallengeTwoFactor();

  useEffect(() => {
    enable2fa(undefined, {
      onSuccess: ({ data }) => {
        setQrCode(svgToBase64(data.qr));
        setRecoveryCodes(data.recoveryCodes);
      },
      onError: () => {
        toast.error("Could not load qr code");
      },
    });
  }, [enable2fa]);

  const onSubmit: SubmitHandler<TwoFactorAuthFormValuesType> = (values) => {
    confirm2fa(values.code);
    challenge2fa(values.code, {
      onSuccess: () => setShowQr(false),
    });
  };

  const hasTwoFaError = hasConfirmFaError || hasChallengeFaError;

  return (
    <>
      <Link
        to={ROUTES.login}
        className="relative flex w-full items-center justify-center"
      >
        <ChevronLeft className="absolute left-0 text-neutral-500" size={16} />
        <p className="text-neutral-600">Back to Log in</p>
      </Link>
      {showQr && (
        <TwoFactorCodeChallenge
          isLoadingQr={isPending}
          isLoadingChallengeCode={isLoadingChallenge}
          hasTwoFaError={hasTwoFaError}
          qrCode={qrCode}
          onSubmit={onSubmit}
        />
      )}

      {!showQr && recoveryCodes.length > 0 && (
        <RecoveryCodes recoveryCodes={recoveryCodes} />
      )}
    </>
  );
};
